import { Boss } from './boss-base';
import { getTexture } from '@/assets/resolver';
import { KR, KRAKEN, armInBlast, buildArms, segWorldPos, type Arm } from './kraken-parts';
import { KrakenGuns, type KrakenRig } from './kraken-guns';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';

export { KRAKEN } from './kraken-parts';

/**
 * C7 — Kraken (deep-sea flavor). Eight waving arms guard an INVULNERABLE
 * mantle: cut every arm, then the body opens up and enrages. Arms
 * periodically LASH at the player (tip blinks red first — in-fiction
 * telegraph). Every hit the Kraken lands DRAINS life back into it.
 */
export class KrakenBoss extends Boss {
  private arms: Arm[];
  private bodyHp = 0;
  private bodyMaxHp = 0;
  private time = 0;
  private driftAngle: number;
  private lashTimer: number = KR.lashInterval * 0.6;
  private lashIndex = 0;
  private readonly guns: KrakenGuns;
  private readonly rig: KrakenRig;
  private stealFlash = 0;
  private readonly aimAt = { x: 0, y: 0 };

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(KRAKEN, level, x, y, homeX, homeY);
    this.allowReinforce = false; // the arm shield IS the gimmick
    this.driftAngle = Math.atan2(y - homeY, x - homeX);
    this.arms = buildArms(this.view, x, y);
    this.guns = new KrakenGuns(this.intensity, this.bulletSpeedMul);
    this.rig = { x, y, driftAngle: 0, time: 0, arms: this.arms, enraged: false };
    this.splitHp();
    this.runners = this.makeRunners([], true); // tier evolve layers (A3)
  }

  /** Mantle share + equal arms — re-split AFTER power scaling (X1). */
  private splitHp(): void {
    this.bodyMaxHp = Math.round(this.maxHp * KR.bodyShare);
    this.bodyHp = this.bodyMaxHp;
    // Unrounded: rounded arms summed past maxHp (bar started above 100%)
    const armHp = (this.maxHp - this.bodyMaxHp) / KR.armCount;
    for (const arm of this.arms) arm.hp = arm.maxHp = armHp;
    this.syncHp();
  }

  /** Power scaling hook: re-split the scaled maxHp across the parts. */
  protected override scaleParts(_mul: number): void {
    this.splitHp();
  }

  private get armsAlive(): number {
    return this.arms.reduce((n, a) => n + (a.alive ? 1 : 0), 0);
  }

  private get enraged(): boolean {
    return this.armsAlive === 0;
  }

  /** Lifesteal (C7): every landed hit feeds the weakest living part. */
  override onDealtDamage(amount: number): void {
    const heal = amount * KR.stealRatio;
    const wounded = this.arms
      .filter((a) => a.alive && a.hp < a.maxHp)
      .sort((a, b) => a.hp - b.hp)[0];
    if (wounded) wounded.hp = Math.min(wounded.maxHp, wounded.hp + heal);
    else this.bodyHp = Math.min(this.bodyMaxHp, this.bodyHp + heal);
    this.syncHp();
    this.stealFlash = 0.35;
  }

  /** Unrounded: the bar drops by exactly the numbers shown. */
  private syncHp(): void {
    this.hp = Math.max(0, this.bodyHp + this.arms.reduce((s, a) => s + (a.alive ? a.hp : 0), 0));
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;
    this.time += dt;
    this.hitFlash -= dt;
    this.stealFlash -= dt;
    this.prevX = this.x;
    this.prevY = this.y;
    const speed = this.levelSpeedMul;

    if (this.enraged) {
      // Enraged mantle slowly hunts the player
      const d = Math.hypot(px - this.x, py - this.y) || 1;
      this.x += ((px - this.x) / d) * KR.chaseSpeed * speed * dt;
      this.y += ((py - this.y) / d) * KR.chaseSpeed * speed * dt;
    } else {
      this.driftAngle += KR.driftSpeed * speed * dt;
      const tx = this.homeX + Math.cos(this.driftAngle) * 150;
      const ty = this.homeY + Math.sin(this.driftAngle * 1.4) * 105;
      this.x += (tx - this.x) * Math.min(1, 0.7 * dt);
      this.y += (ty - this.y) * Math.min(1, 0.7 * dt);
      this.updateArms(dt, px, py);
    }
    const rig = this.rig;
    rig.x = this.x;
    rig.y = this.y;
    rig.driftAngle = this.driftAngle;
    rig.time = this.time;
    rig.enraged = this.enraged;
    this.guns.update(rig, dt, px, py, bullets);
    this.fireRunners(dt, px, py, bullets, telegraphs);
  }

  private updateArms(dt: number, px: number, py: number): void {
    // One arm at a time winds up and lashes at the player (tier tempo)
    this.lashTimer -= dt * this.intensity;
    if (this.lashTimer <= 0) {
      for (let n = 0; n < KR.armCount; n++) {
        const arm = this.arms[(this.lashIndex + n) % KR.armCount]!;
        if (arm.alive && arm.windup <= 0 && arm.lashing <= 0) {
          arm.windup = KR.lashWindup;
          this.lashIndex = (this.lashIndex + n + 1) % KR.armCount;
          break;
        }
      }
      this.lashTimer = KR.lashInterval;
    }

    for (const arm of this.arms) {
      if (!arm.alive) continue;
      if (arm.windup > 0) {
        arm.windup -= dt;
        if (arm.windup <= 0) {
          arm.lashing = KR.lashTime;
          arm.lashAngle = Math.atan2(py - this.y, px - this.x);
        }
      } else if (arm.lashing > 0) {
        arm.lashing -= dt;
      }
      // Reach swells mid-lash, then relaxes back
      const p = arm.lashing > 0 ? Math.sin((1 - arm.lashing / KR.lashTime) * Math.PI) : 0;
      arm.extend = 1 + (KR.lashExtend - 1) * p;

      const wave = Math.sin(this.time * KR.waveSpeed + arm.baseAngle * 3) * KR.waveAmp;
      const angle = arm.lashing > 0
        ? arm.lashAngle
        : arm.baseAngle + this.driftAngle * 0.4 + wave;
      const reach = KR.armBaseR + KR.armSegs * KR.segDist * arm.extend;
      arm.tipX = this.x + Math.cos(angle) * reach;
      arm.tipY = this.y + Math.sin(angle) * reach;
    }
  }

  // ---- Collision: arms first, body only when every arm is gone ----

  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    let hits = 0;
    let dealt = 0;
    for (const arm of this.arms) {
      if (!arm.alive) continue;
      const hurt = (dmg: number): number => {
        const taken = Math.min(Math.max(0, arm.hp), dmg);
        arm.hp -= dmg;
        dealt += taken;
        return taken; // this arm's own loss — the number shows exactly it
      };
      hits += bullets.collideCircle(true, arm.tipX, arm.tipY, KR.tipRadius, hurt);
      for (let s = 0; s < KR.armSegs; s++) {
        const p = segWorldPos(this.x, this.y, arm, s);
        hits += bullets.collideCircle(true, p.x, p.y, KR.segRadius, hurt);
      }
      if (arm.hp <= 0) this.killArm(arm);
    }
    if (this.enraged) {
      hits += bullets.collideCircle(true, this.x, this.y, this.def.radius, (dmg) => {
        const taken = Math.min(Math.max(0, this.bodyHp), dmg);
        this.bodyHp -= dmg;
        dealt += taken;
        this.hitFlash = 0.08;
        return taken;
      });
    } else {
      // Mantle is shielded — bullets clink off without damage
      bullets.collideCircle(true, this.x, this.y, this.def.radius, () => {
        this.hitFlash = 0.08;
      }, false);
    }
    this.finishDamage();
    return { hits, dealt };
  }

  /** Sealed mantle: aim at the nearest living arm tip instead (X7). */
  override aimPoint(px: number, py: number): { x: number; y: number } {
    if (this.enraged) return this;
    let bestD = Infinity;
    for (const a of this.arms) {
      if (!a.alive) continue;
      const d = (a.tipX - px) ** 2 + (a.tipY - py) ** 2;
      if (d < bestD) {
        bestD = d;
        this.aimAt.x = a.tipX;
        this.aimAt.y = a.tipY;
      }
    }
    return bestD < Infinity ? this.aimAt : this;
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    if (Math.hypot(this.x - x, this.y - y) < radius + this.def.radius) return true;
    return armInBlast(this.arms, this.x, this.y, x, y, radius) !== null;
  }

  /** Blasts cut the arm they touch; the sealed mantle shrugs them off (X5). */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (this.dead) return 0;
    let dealt = 0;
    if (this.enraged) {
      if (Math.hypot(this.x - x, this.y - y) >= radius + this.def.radius) return 0;
      this.markAoe(this.x, this.y, this.def.radius);
      dealt = Math.min(this.bodyHp, damage);
      this.bodyHp -= damage;
      this.hitFlash = 0.08;
    } else {
      const arm = armInBlast(this.arms, this.x, this.y, x, y, radius, this.aoeAt);
      if (!arm) return 0;
      dealt = Math.min(arm.hp, damage);
      arm.hp -= damage;
      if (arm.hp <= 0) this.killArm(arm);
    }
    this.finishDamage();
    return dealt;
  }

  override takeDamage(amount: number): boolean {
    this.applyAoe(this.x, this.y, Infinity, amount);
    return this.dead;
  }

  private finishDamage(): void {
    this.syncHp();
    if (this.bodyHp <= 0 && this.armsAlive === 0) this.dead = true;
  }

  private killArm(arm: Arm): void {
    if (!arm.alive) return;
    arm.alive = false;
    arm.tip.visible = false;
    for (const s of arm.segs) s.visible = false;
    const points = arm.segs.map((_, i) => segWorldPos(this.x, this.y, arm, i));
    this.events?.chain([...points, { x: arm.tipX, y: arm.tipY }], BOSS_FEEDBACK.krakenArm);
  }

  override touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    if (Math.hypot(this.x - px, this.y - py) < this.def.radius + playerRadius) return true;
    for (const arm of this.arms) {
      if (!arm.alive) continue;
      if (Math.hypot(arm.tipX - px, arm.tipY - py) < KR.tipRadius + playerRadius) return true;
      for (let s = 0; s < KR.armSegs; s++) {
        const p = segWorldPos(this.x, this.y, arm, s);
        if (Math.hypot(p.x - px, p.y - py) < KR.segRadius + playerRadius) return true;
      }
    }
    return false;
  }

  override render(alpha: number): void {
    super.render(alpha); // mantle position, damage tint, cracks, punch
    this.body.texture = getTexture('boss/kraken/body', this.enraged ? 1 : 0);
    if (this.stealFlash > 0) this.body.tint = 0x7ec850; // life drain glow

    const ix = this.prevX + (this.x - this.prevX) * alpha;
    const iy = this.prevY + (this.y - this.prevY) * alpha;
    for (const arm of this.arms) {
      if (!arm.alive) continue;
      // Sprites live in the view (anchored at the mantle) → relative coords
      for (let s = 0; s < KR.armSegs; s++) {
        const p = segWorldPos(this.x, this.y, arm, s);
        arm.segs[s]!.position.set(p.x - ix, p.y - iy);
      }
      arm.tip.position.set(arm.tipX - ix, arm.tipY - iy);
      arm.tip.rotation = Math.atan2(arm.tipY - this.y, arm.tipX - this.x);
      // In-fiction lash telegraph: tip blinks red during wind-up
      const blink = arm.windup > 0 && Math.sin(this.time * 40) > 0;
      arm.tip.tint = blink ? 0xff5544 : 0xffffff;
    }
  }
}
